import React, { useRef, useState } from 'react';
import MenuAtom, { MenuItem, MenuHandle } from '../../atoms/menu/menu'; // Adjust import path
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Sphere } from 'types/global';

interface PerspectiveMenuProps {
  perspectives: Sphere[];
  perspectiveSelected?: (perspectiveName: string) => void;
  className?: string; // Optional for custom styling
}

const PerspectiveMenu: React.FC<PerspectiveMenuProps> = ({
  perspectives,
  perspectiveSelected,
  className,
}) => {
  const menuRef = useRef<MenuHandle>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Local state to store the dropdown-selected perspective name
  const [selectedPerspectiveName, setSelectedPerspectiveName] =
    useState<string>('');

  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    // Trigger ripple effect
    const target = event.currentTarget as HTMLElement;
    const button = target.closest('.perspective-menu');
    if (button) {
      button.classList.add('ripple-active');
      setTimeout(() => button.classList.remove('ripple-active'), 600);
    }

    // Toggle the menu first
    menuRef.current?.toggle(event);
    setError(null);

    try {
      // Sort perspectives by name (or fallback to an empty string)
      const sortedPerspectives = [...perspectives].sort((a, b) =>
        (a.properties.name ?? '').localeCompare(b.properties.name ?? '')
      );

      // Map perspectives to MenuItem objects
      const contentItems: MenuItem[] = sortedPerspectives.map((perspective) => {
        const handleClick = () => {
          // Update the local state with the selected perspective name
          setSelectedPerspectiveName(
            perspective.properties.name ?? 'Unnamed Perspective'
          );
          perspectiveSelected?.(perspective.id);
        };

        return {
          template: () => (
            <div
              className="flex align-items-center gap-2"
              style={{ cursor: 'pointer' }}
              onClick={handleClick}
            >
              <i className="perspective-icon"></i>
              <span>{perspective.properties.name ?? 'Unnamed Perspective'}</span>
            </div>
          ),
          command: handleClick,
        };
      });

      setMenuItems(contentItems);
    } catch (error: unknown) {
      console.error('Error fetching perspectives:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch perspectives.';
      setError(errorMessage);
    }
  };

  return (
    <div className={`perspective-menu ${className ?? ''}`}>
      <div
        onClick={handleClick}
        style={{
          cursor: 'pointer',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <i className="pi perspective-icon mt-1 ml-1"></i>
        {selectedPerspectiveName && (
          <span className="selected-perspective-name">
            {selectedPerspectiveName}
          </span>
        )}
      </div>
      <MenuAtom
        model={menuItems}
        popup
        ref={menuRef}
        appendTo={document.body}
        className="custom-dropdown-menu"
      />
      {error && <div style={{ color: 'red', marginTop: '8px' }}>{error}</div>}
    </div>
  );
};

export default PerspectiveMenu;
